import { assert, BadRequestError, InternalError } from '@fragment/error-utils';
import type { SafeString } from '@fragment/types/strings';
import { SafeStringSchema } from '@fragment/types/strings';
import { parseWithError } from '@fragment/zod-utils/src';

import { base64Decode, base64Encode } from '../encode';

type DecodedBankAccountId = {
  linkId: string;
  accountId: SafeString;
};
type BaseBankAccount = {
  linkId: string;
  accountId: SafeString;
};
export const encodeBankAccountId = (account: BaseBankAccount): string =>
  base64Encode(`at:${account.accountId}:lk:${account.linkId}`);

export const decodeBankAccountId = (encoded: string): DecodedBankAccountId => {
  try {
    const decoded = base64Decode(encoded);

    const bits = decoded.split(':');
    assert(bits[0] === 'at', BadRequestError, {
      message: `Provided with an invalid external account id: ${encoded}`,
    });
    assert(bits[2] === 'lk', BadRequestError, {
      message: `Provided with an invalid external account id: ${encoded}`,
    });

    return {
      accountId: parseWithError(SafeStringSchema, bits[1], InternalError),
      linkId: bits[3],
    };
  } catch (error) {
    // SafeString parsing might throw an internal error -- this is not a user error.
    if (error instanceof InternalError) throw error;
    // Otherwise we failed to decode this, which means it's probably not an internal ID.
    throw new BadRequestError(
      `ID ${encoded} is not a valid External Account ID. Did you mean to pass \`externalId\` instead?` +
        `See https://fragment.dev/api-reference/api-types#matchinput-types-externalaccountmatchinput for usage guidelines.`
    );
  }
};

type DecodedBankLinkId = {
  linkId: SafeString;
};
type BaseBankLink = {
  id: string;
};
export const encodeBankLinkId = (link: BaseBankLink): string =>
  base64Encode(`lk:${link.id}`);
export const decodeBankLinkId = (encoded: string): DecodedBankLinkId => {
  const decoded = base64Decode(encoded);

  const bits = decoded.split(':');
  assert(bits[0] === 'lk', BadRequestError, {
    message: `Provided with an invalid link id: ${encoded}`,
  });

  return {
    linkId: SafeStringSchema.parse(bits[1]),
  };
};
